import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  HeartIcon,
  CalendarDaysIcon,
  MapPinIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import dressService from '../services/dress';
import dressLikesService from '../services/dressLikes';
import AuthenticatedImage from '../components/AuthenticatedImage';
import HeartAnimation from '../components/HeartAnimation';
import { Dress } from '../types';
import { useAuth } from '../contexts/AuthContext';

const CONTACT_URL = 'https://www.theproposal.com.sg/contact/';

const DressDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [dress, setDress] = useState<Dress | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [showHeartAnimation, setShowHeartAnimation] = useState(false);
  const [likeProcessing, setLikeProcessing] = useState(false);
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);
  const lastTapRef = useRef<number>(0);

  const loadDress = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const data = await dressService.getDress(id);
      setDress(data);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      } else {
        setError('We couldn’t load this dress. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadLikeState = useCallback(async () => {
    if (!user || !id) return;
    try {
      const likedIds = await dressLikesService.checkLikes([id]);
      setIsLiked(likedIds.includes(id));
    } catch (err) {
      // Non-fatal — just leave isLiked as false
    }
  }, [user, id]);

  useEffect(() => {
    loadDress();
  }, [loadDress]);

  useEffect(() => {
    loadLikeState();
  }, [loadLikeState]);

  const toggleLike = useCallback(async () => {
    if (!user || !dress || likeProcessing) return;
    const next = !isLiked;
    setLikeProcessing(true);
    setIsLiked(next);
    if (next) setShowHeartAnimation(true);
    try {
      if (next) {
        await dressLikesService.likeDress(dress.id);
      } else {
        await dressLikesService.unlikeDress(dress.id);
      }
    } catch (err: any) {
      setIsLiked(!next);
      if (err?.response?.status === 403 && err?.response?.data?.code === 'BIZ_002') {
        alert('This dress requires exclusive access. Please contact us for more information.');
      } else {
        alert('Could not update your liked dresses. Please try again.');
      }
    } finally {
      setLikeProcessing(false);
    }
  }, [user, dress, isLiked, likeProcessing]);

  const handleImageTap = (e: React.MouseEvent | React.TouchEvent) => {
    if (!user) return;
    const now = Date.now();
    const delta = now - lastTapRef.current;
    if (delta < 300 && delta > 0) {
      e.preventDefault();
      toggleLike();
    }
    lastTapRef.current = now;
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareData = {
      title: dress ? `${dress.name} — ${dress.designer?.name ?? ''}`.trim() : 'Bridal Journey',
      text: dress?.description || 'Take a look at this dress on Bridal Journey.',
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      setShareFeedback('Link copied!');
      setTimeout(() => setShareFeedback(null), 2000);
    } catch {
      // User cancelled share — ignore
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="px-4 py-4">
          <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" />
        </div>
        <div className="aspect-[3/4] sm:aspect-[4/5] max-w-3xl mx-auto bg-gray-200 animate-pulse" />
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-3">
          <div className="h-4 w-1/3 bg-gray-200 rounded animate-pulse" />
          <div className="h-8 w-2/3 bg-gray-200 rounded animate-pulse" />
          <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
          <div className="h-4 w-5/6 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (notFound || !dress) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <header className="bg-white shadow-sm sticky top-0 z-40">
          <div className="px-4 py-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Go back"
            >
              <ArrowLeftIcon className="h-6 w-6 text-gray-600" />
            </button>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-serif text-gray-900 mb-2">
              {notFound ? 'Dress not available' : 'Something went wrong'}
            </h1>
            <p className="text-gray-600 mb-6">
              {notFound
                ? 'This dress isn’t available to view right now. It may have been removed or requires a different access level.'
                : error}
            </p>
            <div className="flex gap-3 justify-center">
              <Link to="/home/catalog" className="btn-primary">
                Browse catalog
              </Link>
              {!notFound && (
                <button onClick={loadDress} className="btn-secondary">
                  Try again
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const categories = dress.dressCategories ?? [];
  const locations = dress.locations ?? [];

  return (
    <div className="min-h-screen bg-gray-50 pb-28 sm:pb-12">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Go back"
          >
            <ArrowLeftIcon className="h-6 w-6 text-gray-600" />
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={handleShare}
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Share dress"
            >
              <ShareIcon className="h-5 w-5 text-gray-600" />
            </button>
            {user && (
              <button
                onClick={toggleLike}
                disabled={likeProcessing}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
                aria-label={isLiked ? 'Unlike dress' : 'Like dress'}
              >
                {isLiked ? (
                  <HeartIconSolid className="h-6 w-6 text-red-500" />
                ) : (
                  <HeartIcon className="h-6 w-6 text-gray-600" />
                )}
              </button>
            )}
          </div>
        </div>
        {shareFeedback && (
          <div className="px-4 pb-2 text-sm text-primary-700">{shareFeedback}</div>
        )}
      </header>

      <div className="max-w-3xl mx-auto md:py-6 md:px-4">
        {/* Image */}
        <div
          className="relative aspect-[3/4] sm:aspect-[4/5] bg-gray-100 overflow-hidden md:rounded-lg"
          onClick={handleImageTap}
          onTouchEnd={handleImageTap}
        >
          {dress.imageUrl ? (
            <AuthenticatedImage
              src={dress.imageUrl}
              alt={dress.name}
              className="object-cover w-full h-full"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-gray-400">
              <svg className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          )}
          {showHeartAnimation && (
            <HeartAnimation onComplete={() => setShowHeartAnimation(false)} />
          )}
        </div>

        {/* Details */}
        <div className="bg-white md:rounded-lg md:mt-6 px-4 md:px-6 py-6">
          {dress.designer && (
            <Link
              to={`/home/designers/${dress.designer.id}`}
              className="text-sm font-bold text-black uppercase tracking-wide hover:underline"
            >
              {dress.designer.name}
            </Link>
          )}
          <h1 className="mt-2 text-3xl font-serif text-gray-900">{dress.name}</h1>

          {categories.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {categories.map((c) => (
                <span
                  key={c.id}
                  className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-xs font-medium text-gray-700 uppercase tracking-wide"
                >
                  {c.displayName || c.name}
                </span>
              ))}
            </div>
          )}

          {dress.description && (
            <p className="mt-6 text-gray-700 leading-relaxed whitespace-pre-line">
              {dress.description}
            </p>
          )}

          {locations.length > 0 && (
            <div className="mt-6 flex items-start gap-2 text-sm text-gray-600">
              <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <span>
                Available at:{' '}
                <span className="font-medium text-gray-800">
                  {locations.map((l) => l.name).join(', ')}
                </span>
              </span>
            </div>
          )}

          {/* CTAs */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <a
              href={CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors"
            >
              <CalendarDaysIcon className="h-5 w-5" />
              Book an appointment
            </a>
            {user && (
              <button
                onClick={toggleLike}
                disabled={likeProcessing}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-gray-300 bg-white text-gray-800 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {isLiked ? (
                  <>
                    <HeartIconSolid className="h-5 w-5 text-red-500" />
                    Saved to liked dresses
                  </>
                ) : (
                  <>
                    <HeartIcon className="h-5 w-5" />
                    Add to liked dresses
                  </>
                )}
              </button>
            )}
          </div>

          {user && (
            <p className="mt-4 text-xs text-gray-500">
              View all your saved dresses in your{' '}
              <Link to="/home/liked-dresses" className="underline hover:text-gray-700">
                Liked Dresses
              </Link>{' '}
              collection.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DressDetail;
